/**
 * Relief as the tiles see it (ADR 0020), shared by the world generator and
 * everything that draws the world.
 *
 * Heights live on the grid's vertices: a vertex stands as high as the
 * highest of its four tiles. A south face is two cells tall, the edge and
 * then the face under it; north, east and west edges are a single cell.
 *
 * A cell is drawn from the 4×4 vertices around it (its window). build:cliffs
 * draws one tile for every window the zones hold (`CLIFF_WINDOWS`, written
 * with the sheets), so no cell ever wears a tile meant for another shape.
 */
import { CLIFF_WINDOWS } from '../data/cliff-windows.generated.js';
export { CLIFF_WINDOWS };
/** Height of the vertex at the top-left corner of tile (vx, vy). */
export function vertexLevel(levelAt, vx, vy) {
    return Math.max(levelAt(vx - 1, vy - 1), levelAt(vx, vy - 1), levelAt(vx - 1, vy), levelAt(vx, vy));
}
/** The four corner heights of tile (x, y): NW, NE, SW, SE. */
export function cornerLevels(levelAt, x, y) {
    return [
        vertexLevel(levelAt, x, y),
        vertexLevel(levelAt, x + 1, y),
        vertexLevel(levelAt, x, y + 1),
        vertexLevel(levelAt, x + 1, y + 1),
    ];
}
/**
 * Whether the cell is part of a cliff: its corners stand at two heights, or
 * it is the face under a south edge. Such a cell is drawn from the cliff
 * tileset and, unless stairs climb it, closed to walking.
 */
export function isCliffCell(levelAt, x, y) {
    const corners = cornerLevels(levelAt, x, y);
    if (corners.some((c) => c !== corners[0]))
        return true;
    const base = corners[0];
    return vertexLevel(levelAt, x, y - 1) > base || vertexLevel(levelAt, x + 1, y - 1) > base;
}
/**
 * The 4×4 vertices around cell (x, y), row by row, each as high above the
 * lowest of them: 0, 1, or 2 where a terrace stands on a narrower shelf.
 * That is all a tile needs to draw its part of every cliff that crosses it.
 */
export function cliffWindow(levelAt, x, y) {
    const window = [];
    for (let dy = -1; dy <= 2; dy += 1) {
        for (let dx = -1; dx <= 2; dx += 1)
            window.push(vertexLevel(levelAt, x + dx, y + dy));
    }
    const floor = Math.min(...window);
    return window.map((level) => level - floor);
}
/** A window as the vocabulary knows it: its sixteen vertices, row by row. */
export const windowKey = (window) => window.join('');
/**
 * Windows every cliff sheet draws, whatever the world holds: the plain
 * sides (the upper one never comes from `cliffWindow`, whose lowest vertex
 * is 0: it is the summit's stone) and the straight south face the stairs
 * are cut from, its edge cell and the face cell under it.
 */
export const PLAIN_LOWER_WINDOW = '0000000000000000';
export const PLAIN_UPPER_WINDOW = '1111111111111111';
export const SOUTH_EDGE_WINDOW = '1111111100000000';
export const SOUTH_FACE_WINDOW = '1111000000000000';
/** Tiles per row of a cliff sheet (build:cliffs). */
export const CLIFF_COLUMNS = 20;
const INDEX = new Map(CLIFF_WINDOWS.map((key, index) => [key, index]));
const CENTRE = new Set([5, 6, 9, 10]);
const nearest = new Map();
/** Whether the sheets draw this very window, not a stand-in. */
export function hasCliffTile(window) {
    return INDEX.has(windowKey(window));
}
/**
 * The tile of a cliff sheet that draws a window. Every window of every zone
 * has its own tile (a test holds build:cliffs to it); one the sheets do not
 * know yet, after a change of the generator, borrows the closest: the four
 * corners weigh most, the ring breaks ties.
 */
export function cliffTileOf(window) {
    const key = windowKey(window);
    const exact = INDEX.get(key) ?? nearest.get(key);
    if (exact !== undefined)
        return exact;
    let best = 0;
    let bestScore = -Infinity;
    CLIFF_WINDOWS.forEach((candidate, index) => {
        let score = 0;
        for (let k = 0; k < 16; k += 1) {
            const hit = candidate.charCodeAt(k) - 48 === window[k];
            if (CENTRE.has(k))
                score += hit ? 10 : -30;
            else
                score += hit ? 1 : -1;
        }
        if (score > bestScore) {
            bestScore = score;
            best = index;
        }
    });
    nearest.set(key, best);
    return best;
}
//# sourceMappingURL=relief.js.map